import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Text,
  Icon,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
  VStack,
} from "@chakra-ui/react";
import {
  FaWarehouse,
  FaBoxes,
  FaCheckCircle,
  FaTimesCircle,
  FaClipboardList,
} from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import {
  GaraazCopyText,
  GaraazInputSearchBar,
} from "@svan-garaaz/reactsharedlib";
import { GroupedAvailability, SupplierInfo } from "./utils/typesenseUtils";
import { SupplierProgressBadge, SupplierText } from "./SupplierBadge";
import { fetchAvailabilityData } from "./utils/typesenseUtils";
import { useGetOrderMediaConfig } from "../../views/PlaceOrderTest/queryHooks";

interface SearchCriteria {
  parts: Array<{
    partNumber: string;
    rootPartNumber: string;
    quantity: number;
    partName: string;
  }>;
  customerGeoPoint?: { latitude: number; longitude: number };
  orderNumber?: string;
}

enum SortOptions {
  SEQUENCE = "sequence",
  QUANTITY_COVERAGE = "quantityCoverage",
  SUPPLIER_COUNT = "supplierCount",
  COVERAGE_DISTANCE = "coverageDistance",
}

const sortOptions = [
  { label: "Sequence (Part Order)", value: SortOptions.SEQUENCE },
  { label: "Quantity Coverage", value: SortOptions.QUANTITY_COVERAGE },
  { label: "Supplier Count", value: SortOptions.SUPPLIER_COUNT },
  { label: "Coverage Distance", value: SortOptions.COVERAGE_DISTANCE },
];

const formatDistance = (distanceMeters?: number): string => {
  if (distanceMeters === undefined) return "N/A";
  const distanceKm = distanceMeters / 1000;
  if (distanceKm < 1) {
    return `${distanceMeters.toFixed(0)}m`;
  }
  return `${distanceKm.toFixed(1)}km`;
};

const SupplierRow: React.FC<{ supplier: SupplierInfo }> = ({ supplier }) => (
  <Tr fontSize="sm">
    <Td px={3} py={2}>
      <VStack align="start" spacing={1}>
        <Text fontWeight="600" color="gray.800" fontSize="sm">
          {supplier.supplierDetails?.businessName || supplier.supplierName}
        </Text>
        {supplier.supplierDetails?.address && (
          <Text fontSize="xs" color="gray.500">
            {supplier.supplierDetails.address}
          </Text>
        )}
      </VStack>
    </Td>
    <Td px={3} py={2} textAlign="center">
      <Text
        fontWeight="600"
        fontSize="sm"
        color={supplier.familyQuantity > 0 ? "green.600" : "red.500"}
      >
        {supplier.familyQuantity}
      </Text>
    </Td>
    <Td px={3} py={2} textAlign="center">
      <Text fontSize="sm" fontWeight="600" color="blue.600">
        {formatDistance(supplier.distanceMeters)}
      </Text>
    </Td>
  </Tr>
);

const AvailabilityPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [availabilityData, setAvailabilityData] = useState<
    GroupedAvailability[]
  >([]);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [sortField, setSortField] = useState<SortOptions>(
    SortOptions.QUANTITY_COVERAGE
  );
  const { data: mobileConfigs } = useGetOrderMediaConfig();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const criteriaParam = searchParams.get("criteria");

        if (!criteriaParam) {
          console.error(
            "No search criteria provided. Please use the availability button from the supplier discovery page."
          );
          return;
        }

        const searchCriteria: SearchCriteria = JSON.parse(
          decodeURIComponent(criteriaParam)
        );

        setOrderNumber(searchCriteria?.orderNumber || null);

        if (!searchCriteria.parts || searchCriteria.parts.length === 0) {
          console.error("No parts found in search criteria.");
          return;
        }

        const data = await fetchAvailabilityData(
          searchCriteria.parts,
          {
            typesense_api_key:
              mobileConfigs?.typesense_api_key_supplierinventories,
            typesense_host: mobileConfigs?.typesense_host_supplierinventories,
            supplier_discovery_suppliers_limit:
              mobileConfigs?.supplier_discovery_suppliers_limit,
            supplier_discovery_parts_limit:
              mobileConfigs?.supplier_discovery_parts_limit,
          },
          searchCriteria.customerGeoPoint
        );

        setAvailabilityData(data);
      } catch (err) {
        console.error("Error fetching availability data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (mobileConfigs) {
      fetchData();
    }
  }, [searchParams, mobileConfigs]);

  const sortedAndFilteredData = useMemo(() => {
    let filtered = [...availabilityData];

    if (searchInput.trim()) {
      const searchTerm = searchInput.toLowerCase();
      filtered = filtered.filter(
        (part) =>
          part.partNumber.toLowerCase().includes(searchTerm) ||
          part.rootPartNumber.toLowerCase().includes(searchTerm) ||
          part.partName.toLowerCase().includes(searchTerm)
      );
    }

    filtered.sort((a, b) => {
      switch (sortField) {
        case SortOptions.SEQUENCE:
          return 0;
        case SortOptions.QUANTITY_COVERAGE:
          return b.quantityFulfilledPercentage - a.quantityFulfilledPercentage;
        case SortOptions.SUPPLIER_COUNT:
          return a.supplierCoverageCount - b.supplierCoverageCount;
        case SortOptions.COVERAGE_DISTANCE:
          if (
            a.coverageDistanceKm === undefined &&
            b.coverageDistanceKm === undefined
          )
            return 0;
          if (a.coverageDistanceKm === undefined) return 1;
          if (b.coverageDistanceKm === undefined) return -1;
          return a.coverageDistanceKm - b.coverageDistanceKm;
        default:
          return 0;
      }
    });

    return filtered;
  }, [availabilityData, searchInput, sortField]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="100vh"
        bg="#F9FAFB"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600" fontSize="lg">
            Loading part availability data...
          </Text>
        </VStack>
      </Box>
    );
  }

  if (!availabilityData || availabilityData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="100vh"
        bg="#F9FAFB"
      >
        <VStack spacing={4}>
          <Icon as={FaWarehouse} boxSize={16} color="gray.400" />
          <Text color="gray.600" fontSize="lg">
            No availability data found
          </Text>
        </VStack>
      </Box>
    );
  }

  const availablePartsCount = sortedAndFilteredData.filter(
    (part) => part.supplierCoverageCount > 0
  ).length;
  const unavailablePartsCount = sortedAndFilteredData.filter(
    (part) => part.supplierCoverageCount === 0
  ).length;

  return (
    <Box minH="100vh" bg="#F9FAFB">
      <Box
        bgGradient="linear(to-r, #2563eb, #7c3aed)"
        color="white"
        py={4}
        px={6}
      >
        <HStack align="start" justify="space-between" spacing={4}>
          <VStack align="start" spacing={1}>
            <Text
              fontSize="3xl"
              fontWeight="900"
              fontFamily="'Nunito','Inter', sans-serif"
            >
              Part Availability Check
            </Text>
            <Text
              fontSize="0.95rem"
              color="whiteAlpha.700"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="500"
              letterSpacing="-.25px"
            >
              Real-time inventory availability across supplier network
            </Text>
            {orderNumber && (
              <HStack spacing={2} mt={2}>
                <Text
                  fontSize="sm"
                  color="whiteAlpha.800"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                >
                  Order No:
                </Text>
                <Text
                  fontSize="sm"
                  color="white"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                >
                  {orderNumber}
                </Text>
                <GaraazCopyText
                  text={orderNumber}
                  popOverMessage="Order Number Copied"
                />
              </HStack>
            )}
          </VStack>

          <HStack spacing={6}>
            <Box
              bg="whiteAlpha.200"
              px={4}
              py={3}
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.300"
              backdropFilter="blur(10px)"
            >
              <HStack spacing={3}>
                <Icon as={FaClipboardList} color="white" boxSize={5} />
                <VStack spacing={0} align="center">
                  <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500">
                    Total
                  </Text>
                  <Text fontSize="xl" fontWeight="700" color="white">
                    {availabilityData.length}
                  </Text>
                </VStack>
              </HStack>
            </Box>
            <Box
              bg="whiteAlpha.200"
              px={4}
              py={3}
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.300"
              backdropFilter="blur(10px)"
            >
              <HStack spacing={3}>
                <Icon as={FaCheckCircle} color="white" boxSize={5} />
                <VStack spacing={0} align="center">
                  <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500">
                    Available
                  </Text>
                  <Text fontSize="xl" fontWeight="700" color="white">
                    {availablePartsCount}
                  </Text>
                </VStack>
              </HStack>
            </Box>
            <Box
              bg="whiteAlpha.200"
              px={4}
              py={3}
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.300"
              backdropFilter="blur(10px)"
            >
              <HStack spacing={3}>
                <Icon as={FaTimesCircle} color="white" boxSize={5} />
                <VStack spacing={0} align="center">
                  <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500">
                    Unavailable
                  </Text>
                  <Text fontSize="xl" fontWeight="700" color="white">
                    {unavailablePartsCount}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </HStack>
        </HStack>
      </Box>

      <Box p={6}>
        <Box mt={-6} mb={2} fontSize="sm">
          <GaraazInputSearchBar
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            sortField={sortField}
            setSortField={setSortField}
            sortOptions={sortOptions}
            placeholder="Search by part number"
            showSortControl={true}
          />
        </Box>

        {sortedAndFilteredData.length === 0 ? (
          <Box textAlign="center" py={12}>
            <Icon as={FaWarehouse} boxSize={16} color="gray.400" mb={4} />
            <SupplierText textStyle="subtitle">
              {availabilityData.length === 0
                ? "No parts found with available inventory from suppliers."
                : "No parts match your search criteria."}
            </SupplierText>
          </Box>
        ) : (
          <Box borderRadius="xl" overflow="hidden" boxShadow="md" bg="white">
            <Box maxH="75vh" overflowY="scroll" overflowX="auto">
              <Table
                fontFamily="'Nunito','Inter', sans-serif"
                variant="simple"
                size="md"
              >
                <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th
                      textTransform="none"
                      color="gray.600"
                      width="15%"
                      fontFamily="'Nunito','Inter', sans-serif"
                      letterSpacing={0}
                    >
                      <HStack spacing={2}>
                        <FaWarehouse color="#2563eb" />
                        <Text fontSize="0.95rem" fontWeight="700">
                          Part Info
                        </Text>
                      </HStack>
                    </Th>
                    <Th
                      textTransform="none"
                      textAlign="center"
                      color="gray.600"
                      width="15%"
                      fontFamily="'Nunito','Inter', sans-serif"
                      letterSpacing={0}
                    >
                      <SupplierText textStyle="label">
                        Requested Qty
                      </SupplierText>
                    </Th>
                    <Th
                      textTransform="none"
                      textAlign="center"
                      color="gray.600"
                      width="15%"
                      fontFamily="'Nunito','Inter', sans-serif"
                      letterSpacing={0}
                    >
                      <SupplierText textStyle="label">Coverage %</SupplierText>
                    </Th>
                    <Th
                      textTransform="none"
                      textAlign="center"
                      color="gray.600"
                      width="15%"
                      fontFamily="'Nunito','Inter', sans-serif"
                      letterSpacing={0}
                    >
                      <SupplierText textStyle="label">
                        Contributing Supplier
                      </SupplierText>
                    </Th>
                    <Th
                      textTransform="none"
                      textAlign="center"
                      color="gray.600"
                      width="15%"
                      fontFamily="'Nunito','Inter', sans-serif"
                      letterSpacing={0}
                    >
                      <SupplierText textStyle="label">
                        Coverage Distance
                      </SupplierText>
                    </Th>
                    <Th
                      textTransform="none"
                      textAlign="center"
                      color="gray.600"
                      width="25%"
                      fontFamily="'Nunito','Inter', sans-serif"
                      letterSpacing={0}
                    >
                      <HStack justifyContent="center" spacing={2}>
                        <FaBoxes color="#2563eb" />
                        <SupplierText textStyle="label">Suppliers</SupplierText>
                      </HStack>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedAndFilteredData.map((part) => (
                    <Tr key={part.groupKey} _hover={{ bg: "gray.50" }}>
                      <Td px={4} py={4} width="15%">
                        <VStack align="start" spacing={2}>
                          <HStack spacing={2}>
                            <SupplierText textStyle="title">
                              {part.partNumber}
                            </SupplierText>
                            <GaraazCopyText
                              text={part.partNumber}
                              popOverMessage="Part No Copied"
                            />
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            {part.partName || "Part Name Not Available"}
                          </Text>
                        </VStack>
                      </Td>
                      <Td textAlign="center" px={4} py={4} width="15%">
                        <Text fontSize="sm" fontWeight="600">
                          {part.requestedQuantity}
                        </Text>
                      </Td>

                      <Td textAlign="center" px={4} py={4} width="15%">
                        <SupplierProgressBadge
                          type="quantity"
                          value={part.quantityFulfilledPercentage}
                        />
                      </Td>

                      <Td textAlign="center" px={4} py={4} width="15%">
                        <Text fontSize="sm" fontWeight="600" color="purple.600">
                          {part.supplierCoverageCount}
                        </Text>
                      </Td>

                      <Td textAlign="center" px={4} py={4} width="15%">
                        <HStack justify="center" spacing={1}>
                          {part.coverageDistanceKm !== undefined && (
                            <MdLocationOn color="green" size="0.8rem" />
                          )}
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color={
                              part.coverageDistanceKm !== undefined
                                ? "green.600"
                                : "gray.500"
                            }
                          >
                            {part.coverageDistanceKm !== undefined
                              ? `${part.coverageDistanceKm.toFixed(1)} km`
                              : part.supplierCoverageCount === 0
                              ? "Not Covered"
                              : "N/A"}
                          </Text>
                        </HStack>
                      </Td>

                      <Td px={4} py={4} width="30%">
                        <Accordion allowToggle size="sm">
                          <AccordionItem border="none">
                            <AccordionButton
                              px={3}
                              py={2}
                              borderRadius="md"
                              bg="blue.50"
                              _hover={{ bg: "blue.100" }}
                              _expanded={{ bg: "blue.100" }}
                            >
                              <Box flex="1" textAlign="left">
                                <Text
                                  fontSize="sm"
                                  fontWeight="600"
                                  color="blue.700"
                                >
                                  {part.totalSuppliers === 0
                                    ? "No Suppliers"
                                    : `${part.totalSuppliers} Suppliers Found`}
                                </Text>
                              </Box>
                              <AccordionIcon color="blue.700" />
                            </AccordionButton>
                            <AccordionPanel pb={4} px={0}>
                              <Box
                                maxH="200px"
                                overflowY="auto"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                              >
                                <Table size="sm" variant="simple" width="100%">
                                  <Thead
                                    bg="gray.50"
                                    position="sticky"
                                    top={0}
                                    zIndex={1}
                                  >
                                    <Tr>
                                      <Th fontSize="xs" py={2} px={3}>
                                        Supplier
                                      </Th>
                                      <Th
                                        fontSize="xs"
                                        py={2}
                                        px={3}
                                        textAlign="center"
                                      >
                                        Available
                                      </Th>
                                      <Th
                                        fontSize="xs"
                                        py={2}
                                        px={3}
                                        textAlign="center"
                                      >
                                        Distance
                                      </Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {part.suppliersHavingPart?.map(
                                      (supplier, supplierIndex) => (
                                        <SupplierRow
                                          key={`${supplier.supplierId}-${supplierIndex}`}
                                          supplier={supplier}
                                        />
                                      )
                                    )}
                                  </Tbody>
                                </Table>
                              </Box>
                            </AccordionPanel>
                          </AccordionItem>
                        </Accordion>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AvailabilityPage;
